import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import logo from "../assets/logo.svg";
import "./ReportsDisplay.css";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import Login from "./Login";
import { useStore } from "../Store";
import { Modal, ModalBody } from "reactstrap";
import { getRegionConfig } from "../config/regionConfig";

const PRESIGN_URL =
  "https://vtwyu7hv50.execute-api.ap-south-1.amazonaws.com/default/RBR_report_pre-signed_URL";

const SUGGEST_URL =
  "https://vtwyu7hv50.execute-api.ap-south-1.amazonaws.com/default/suggest";

const REGION = getRegionConfig();

const DEFAULT_MRP = REGION.finalReportMrp;
const DEFAULT_PROMO_PCT = REGION.promoPct;
const DEFAULT_FINAL = REGION.finalReportPrice;
const UNLOCKED_MAX_PAGE = 3; // page index 0-3 = first 4 pages free

const ReportsDisplay = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const defaultLayoutPluginInstance = defaultLayoutPlugin();
  const { state, dispatch: cxtDispatch } = useStore();

  const reportSlugFromState = location.state?.reportSlug || "";

  // Direct links from generated PDFs use:
  // /report-display?slug=<catalogue-slug>
  const reportSlugFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (params.get("slug") || "").trim();
  }, [location.search]);

  const needsCatalogueLookup =
    !!reportSlugFromQuery && !reportSlugFromState;

  const [catalogReport, setCatalogReport] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(needsCatalogueLookup);
  const [catalogError, setCatalogError] = useState("");

  const incomingFileKey = location.state?.fileKey || "";

  // Navigation state continues to take priority. Catalogue values are used
  // only for direct ?slug= links that do not have React router state.
  const incomingReportId =
    location.state?.reportId ||
    catalogReport?.report_id ||
    catalogReport?.reportId ||
    "";

  const incomingReportTitle =
    location.state?.reportTitle ||
    catalogReport?.title ||
    "";

  const incomingPreviewKey =
    location.state?.previewKey ||
    catalogReport?.preview_key ||
    catalogReport?.previewKey ||
    "";

  const incomingFullKey =
    location.state?.fullKey ||
    catalogReport?.full_key ||
    catalogReport?.fullKey ||
    "";

  const incomingCurrency =
    location.state?.currency ||
    catalogReport?.currency ||
    REGION.currencyCode;

  const incomingReportType =
    location.state?.reportType ||
    catalogReport?.report_type ||
    catalogReport?.reportType ||
    "catalogue";

  const incomingPrice = Number(
    location.state?.price ?? catalogReport?.price
  );
  const incomingMrp = Number(
    location.state?.mrp ?? catalogReport?.mrp
  );
  const incomingPromoPct = Number(
    location.state?.promoPct ??
      catalogReport?.promo_pct ??
      catalogReport?.promoPct
  );

  const FINAL =
    Number.isFinite(incomingPrice) && incomingPrice > 0
      ? incomingPrice
      : DEFAULT_FINAL;

  const MRP =
    Number.isFinite(incomingMrp) && incomingMrp > 0
      ? incomingMrp
      : DEFAULT_MRP;

  const PROMO_PCT =
    Number.isFinite(incomingPromoPct) && incomingPromoPct >= 0
      ? incomingPromoPct
      : DEFAULT_PROMO_PCT;

  const currencySymbol =
    incomingCurrency === "INR"
      ? "₹"
      : incomingCurrency === "USD"
      ? "$"
      : incomingCurrency === "GBP"
      ? "£"
      : REGION.currencySymbol;

  const { status = false, email = "", userInfo = {} } = state || {};
  const isLoggedIn = !!userInfo?.isLogin;
  const purchases = userInfo?.purchases || [];

  const derivedSlugFromFileKey = useMemo(() => {
    if (!incomingFileKey) return "";
    const m = incomingFileKey.match(/([a-z0-9_]+)(?:_preview)?\.pdf$/i);
    return m ? m[1] : "";
  }, [incomingFileKey]);

  const reportSlug =
    reportSlugFromState ||
    catalogReport?.slug ||
    reportSlugFromQuery ||
    derivedSlugFromFileKey ||
    "paper_industry";

  const isPurchased = purchases.includes(reportSlug);

  const desiredKey = useMemo(() => {
    if (needsCatalogueLookup && catalogLoading) {
      return "";
    }

    if (isPurchased) {
      return incomingFullKey || `${reportSlug}.pdf`;
    }

    return (
      incomingPreviewKey ||
      incomingFileKey ||
      `${reportSlug}_preview.pdf`
    );
  }, [
    needsCatalogueLookup,
    catalogLoading,
    incomingFileKey,
    incomingPreviewKey,
    incomingFullKey,
    reportSlug,
    isPurchased,
  ]);

  const [openModel, setOpenModel] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState("");
  const [error, setError] = useState("");
  const [localFileKey, setLocalFileKey] = useState(desiredKey);
  const [localReportId, setLocalReportId] = useState(incomingReportId);
  const [currentPage, setCurrentPage] = useState(0);

  // ====== Resolve direct ?slug= links from the catalogue ======
  useEffect(() => {
    if (!needsCatalogueLookup) {
      setCatalogReport(null);
      setCatalogLoading(false);
      setCatalogError("");
      return;
    }

    const controller = new AbortController();

    const parsePayload = (raw) => {
      if (typeof raw?.body === "string") {
        try {
          return JSON.parse(raw.body);
        } catch {
          return {};
        }
      }

      if (raw?.body && typeof raw.body === "object") {
        return raw.body;
      }

      return raw || {};
    };

    const fetchExactCatalogueReport = async () => {
      setCatalogLoading(true);
      setCatalogError("");
      setError("");
      setPdfUrl("");

      try {
        const lookupQueries = [
          reportSlugFromQuery,
          reportSlugFromQuery.replace(/_/g, " "),
        ];

        let exactMatch = null;

        for (const query of lookupQueries) {
          const response = await fetch(SUGGEST_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ q: query, limit: 25 }),
            signal: controller.signal,
          });

          if (!response.ok) {
            const responseText = await response.text();
            throw new Error(
              `Catalogue lookup failed (${response.status}): ${responseText}`
            );
          }

          const raw = await response.json();
          const payload = parsePayload(raw);
          const items = Array.isArray(payload?.items) ? payload.items : [];

          exactMatch = items.find(
            (item) =>
              String(item?.slug || "").trim().toLowerCase() ===
              reportSlugFromQuery.toLowerCase()
          );

          if (exactMatch) break;
        }

        if (!exactMatch) {
          throw new Error(
            "This report could not be found in the catalogue."
          );
        }

        setCatalogReport(exactMatch);
      } catch (lookupError) {
        if (lookupError?.name === "AbortError") return;

        console.error("Direct report catalogue lookup error:", lookupError);
        const message =
          lookupError?.message ||
          "This report could not be loaded from the catalogue.";

        setCatalogReport(null);
        setCatalogError(message);
        setError(message);
        setIsLoading(false);
      } finally {
        if (!controller.signal.aborted) {
          setCatalogLoading(false);
        }
      }
    };

    fetchExactCatalogueReport();

    return () => controller.abort();
  }, [needsCatalogueLookup, reportSlugFromQuery]);

  useEffect(() => {
    setLocalFileKey(desiredKey);
    setLocalReportId(incomingReportId || "");
  }, [desiredKey, incomingReportId]);

  const shouldLock =
    !isPurchased &&
    typeof currentPage === "number" &&
    currentPage > UNLOCKED_MAX_PAGE;

  const formatSlugTitle = (slug) => {
    if (!slug) return "Report Preview";
    return slug
      .replace(/_preview$/i, "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const pageTitle = incomingReportTitle || formatSlugTitle(reportSlug);
  const pageDesc = isPurchased
    ? "You’ve unlocked this report from Rajan Business Reports."
    : "Preview this report and unlock the full version for complete data, analysis, and forecasts.";

  const handlePayment = () => {
    const paymentFileKey = incomingFullKey || `${reportSlug}.pdf`;

    cxtDispatch({
      type: "SET_FILE_REPORT",
      payload: {
        fileKey: paymentFileKey,
        reportId: localReportId,
        reportSlug,
        reportTitle: pageTitle,
        price: FINAL,
        mrp: MRP,
        promoPct: PROMO_PCT,
        currency: incomingCurrency,
        reportType: incomingReportType,
      },
    });

    localStorage.setItem("amount", String(FINAL));
    localStorage.setItem("reportId", localReportId || "");
    localStorage.setItem("fileKey", paymentFileKey);

    if (isLoggedIn) {
      navigate("/payment", {
        replace: true,
        state: {
          fromReport: true,
          amount: FINAL,
          reportId: localReportId,
          fileKey: paymentFileKey,
          reportSlug,
          reportTitle: pageTitle,
          mrp: MRP,
          promoPct: PROMO_PCT,
          currency: incomingCurrency,
          reportType: incomingReportType,
        },
      });
    } else {
      setOpenModel(true);
    }
  };

  const changeStatus = () => {
    setOpenModel(false);
    cxtDispatch({ type: "SET_REPORT_STATUS" });
  };

  useEffect(() => {
    const fetchPresignedUrl = async () => {
      if (catalogLoading) return;

      if (catalogError) {
        setError(catalogError);
        setPdfUrl("");
        setIsLoading(false);
        return;
      }

      if (!localFileKey) {
        setError("No report key determined. Please try again.");
        setPdfUrl("");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError("");

      try {
        const response = await fetch(PRESIGN_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ file_key: localFileKey }),
        });

        if (!response.ok) {
          const txt = await response.text();
          throw new Error(`HTTP ${response.status}: ${txt}`);
        }

        const data = await response.json();

        if (!data?.presigned_url) {
          throw new Error(`No presigned URL returned: ${JSON.stringify(data)}`);
        }

        setPdfUrl(data.presigned_url);
      } catch (err) {
        console.error("Error fetching presigned URL:", err);
        setError("Failed to load report preview. Please try again.");
        setPdfUrl("");
      } finally {
        setIsLoading(false);
      }
    };

    fetchPresignedUrl();
  }, [localFileKey, catalogLoading, catalogError]);

  return (
    <>
      <div className="report-display">
        <nav className="navbar navbar-expand-lg bg-light">
          <div className="container-fluid">
            <div className="nav-left">
              <div className="logo">
                <Link to="/" className="navbar-brand">
                  <img src={logo} alt="" style={{ width: "60px", height: "60px" }} />
                </Link>
              </div>

              <div className="text">
                <p
                  className="report-display-title"
                  style={{ fontSize: "28px", marginBottom: "6px" }}
                >
                  {pageTitle}
                </p>
                <p
                  className="report-display-desc"
                  style={{ marginTop: "0px", width: "70%" }}
                >
                  {pageDesc}
                </p>
              </div>
            </div>

            <button
              className="navbar-toggler"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#navbarSupportedContent"
            >
              <span className="navbar-toggler-icon"></span>
            </button>

            <div className="collapse navbar-collapse" id="navbarSupportedContent">
              <ul className="navbar-nav ms-auto">
                <li
                  className="nav-item"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "14px",
                  }}
                >
                  {!isPurchased && (
                    <div
                      style={{
                        textAlign: "right",
                        lineHeight: 1.1,
                        minWidth: "100px",
                      }}
                    >
                      <div
                        style={{
                          color: "#64748b",
                          fontSize: "12px",
                          textDecoration: "line-through",
                          marginBottom: "3px",
                        }}
                      >
                        {currencySymbol}{MRP.toLocaleString("en-IN")}
                      </div>
                      <div
                        style={{
                          color: "#0f172a",
                          fontSize: "22px",
                          fontWeight: 800,
                        }}
                      >
                        {currencySymbol}{FINAL.toLocaleString("en-IN")}
                      </div>
                      <div
                        style={{
                          color: "#15803d",
                          fontSize: "11px",
                          fontWeight: 700,
                          marginTop: "2px",
                        }}
                      >
                        {PROMO_PCT}% off
                      </div>
                    </div>
                  )}

                  <button
                    className="buy-btn"
                    onClick={handlePayment}
                    style={{ color: "white" }}
                  >
                    {isPurchased ? "PURCHASED" : "BUY NOW"}
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </nav>

        <div
          className="viewer col-md-11 col-sm-11 col-11"
          style={{ position: "relative", minHeight: "80vh" }}
        >
          <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
            {isLoading ? (
              <div className="spinner-border" role="status" style={{ marginTop: "40px" }}>
                <span className="visually-hidden">Loading...</span>
              </div>
            ) : error ? (
              <div className="error-message">{error}</div>
            ) : pdfUrl ? (
              <>
                <Viewer
                  fileUrl={pdfUrl}
                  plugins={[defaultLayoutPluginInstance]}
                  onPageChange={(e) => {
                    setCurrentPage(e.currentPage);
                  }}
                />

                {shouldLock && (
                  <>
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 20,
                        background: "rgba(255,255,255,0.02)",
                      }}
                    />

                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 30,
                        background:
                          "linear-gradient(to bottom, rgba(255,255,255,0.02), rgba(15,23,42,0.18), rgba(15,23,42,0.82))",
                        backdropFilter: "blur(3px)",
                        pointerEvents: "none",
                      }}
                    />

                    <div
                      style={{
                        position: "absolute",
                        left: "50%",
                        bottom: "24px",
                        transform: "translateX(-50%)",
                        zIndex: 40,
                        width: "min(92%, 520px)",
                        borderRadius: "20px",
                        background:
                          "linear-gradient(135deg, #0f172a 0%, #020617 100%)",
                        border: "1px solid rgba(59,130,246,0.35)",
                        boxShadow: "0 20px 45px rgba(15,23,42,0.55)",
                        color: "#fff",
                        padding: "18px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: "16px",
                          alignItems: "flex-start",
                          marginBottom: "10px",
                        }}
                      >
                        <div>
                          <div
                            style={{
                              display: "inline-block",
                              fontSize: "10px",
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: "0.08em",
                              padding: "4px 8px",
                              borderRadius: "999px",
                              background: "rgba(59,130,246,0.15)",
                              border: "1px solid rgba(96,165,250,0.45)",
                              color: "#bfdbfe",
                              marginBottom: "8px",
                            }}
                          >
                            You’ve reached the detailed section
                          </div>

                          <div style={{ fontSize: "16px", fontWeight: 600 }}>
                            Unlock the full report to continue beyond the free preview.
                          </div>
                        </div>

                        <div style={{ textAlign: "right", minWidth: "90px" }}>
                          <div
                            style={{
                              textDecoration: "line-through",
                              color: "#94a3b8",
                              fontSize: "12px",
                            }}
                          >
                            {currencySymbol}{MRP.toLocaleString("en-IN")}
                          </div>
                          <div
                            style={{
                              color: "#fde68a",
                              fontWeight: 700,
                              fontSize: "20px",
                              lineHeight: 1.1,
                            }}
                          >
                            {currencySymbol}{FINAL.toLocaleString("en-IN")}
                          </div>
                          <div style={{ fontSize: "11px", color: "#86efac" }}>
                            {PROMO_PCT}% launch discount
                          </div>
                        </div>
                      </div>

                      <ul
                        style={{
                          margin: "0 0 14px 18px",
                          padding: 0,
                          fontSize: "13px",
                          color: "#e2e8f0",
                          lineHeight: 1.6,
                        }}
                      >
                        <li>Full market size and growth outlook</li>
                        <li>Competitor list, pricing, and business insights</li>
                        <li>Detailed opportunities, risks, and recommendations</li>
                      </ul>

                      <button
                        onClick={handlePayment}
                        style={{
                          width: "100%",
                          border: "none",
                          borderRadius: "12px",
                          padding: "12px 14px",
                          fontWeight: 700,
                          fontSize: "14px",
                          background:
                            "linear-gradient(to right, #fbbf24, #fde68a)",
                          color: "#111827",
                          cursor: "pointer",
                          marginBottom: "10px",
                        }}
                      >
                        Pay & unlock full report — {currencySymbol}{FINAL.toLocaleString("en-IN")}
                      </button>

                      <div
                        style={{
                          textAlign: "center",
                          fontSize: "11px",
                          color: "#cbd5e1",
                        }}
                      >
                        You can still scroll back and read the first 4 preview pages.
                      </div>
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="error-message">
                Failed to load report preview. The preview file may be missing.
              </div>
            )}
          </Worker>
        </div>
      </div>

      <Modal
        isOpen={openModel}
        toggle={() => setOpenModel(false)}
        style={{ maxWidth: "650px", width: "100%", marginTop: "15%" }}
        size="lg"
      >
        <ModalBody>
          <Login onClose={() => setOpenModel(false)} returnTo="/payment" />
          {status && (
            <div style={{ textAlign: "center" }}>
              <p className="success-head">The report has been successfully sent to</p>
              <p className="success-email">{email}</p>
              <button className="btn btn-primary" onClick={changeStatus}>
                Ok
              </button>
            </div>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default ReportsDisplay;
